import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, X, Loader2, Shield, ExternalLink, MapPin, Mail, Globe, Filter, RefreshCw, Image as ImageIcon } from 'lucide-react';
import {
  adminVerify, adminListSubmissions, adminApproveSubmission, adminRejectSubmission,
} from '../services/api';
import AuditPanel from '../components/AuditPanel';
import FeedbackPanel from '../components/FeedbackPanel';

const TOKEN_KEY = 'pdx_admin_token';

const Login = ({ onLogin }) => {
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await adminVerify(token);
      localStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
    } catch (e) {
      setErr('Invalid admin token. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-slate-200 p-6"
      >
        <div className="flex items-center gap-2 mb-1">
          <Shield size={16} className="text-emerald-600" />
          <span className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">Admin</span>
        </div>
        <h1 className="text-[20px] font-semibold text-slate-900">Portland Startup Map</h1>
        <p className="text-[13px] text-slate-500 mt-1">Sign in to review submissions.</p>

        <label className="block mt-5">
          <span className="block text-[12px] font-medium text-slate-700 mb-1">Admin token</span>
          <input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            type="password"
            autoFocus
            placeholder="••••••••"
            className="w-full text-[13.5px] px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
          />
        </label>
        {err && (
          <div className="mt-2 text-[12.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>
        )}
        <button
          type="submit"
          disabled={busy || !token}
          className="mt-4 w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-medium text-[14px] py-2.5 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Sign in
        </button>

        <Link to="/" className="block mt-4 text-center text-[12.5px] text-slate-500 hover:text-slate-800">
          ← Back to map
        </Link>
      </form>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const styles = {
    pending: 'bg-amber-50 text-amber-800 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <span className={`inline-flex items-center text-[10.5px] font-medium px-2 py-0.5 rounded-full border uppercase tracking-wide ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

const SUBMISSION_TABS = ['pending', 'approved', 'rejected', 'all'];

const AdminInner = ({ token, onLogout }) => {
  const [tab, setTab] = useState('pending');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState(new Set());
  const [errors, setErrors] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const all = await adminListSubmissions(token);
      setSubmissions(all);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const visible = submissions.filter((s) => (tab === 'all' ? true : s.status === tab));

  const setBusy = (id, on) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };

  const handleApprove = async (sub) => {
    setBusy(sub.id, true);
    setErrors((e) => ({ ...e, [sub.id]: null }));
    try {
      await adminApproveSubmission(token, sub.id, {
        sector: sub.sector || 'Other',
        neighborhood: 'Portland',
        stage: 'seed',
        badge: 'Submitted',
        color: '#0F172A',
      });
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Approve failed';
      setErrors((er) => ({ ...er, [sub.id]: msg }));
    } finally {
      setBusy(sub.id, false);
    }
  };

  const handleReject = async (sub) => {
    setBusy(sub.id, true);
    setErrors((e) => ({ ...e, [sub.id]: null }));
    try {
      await adminRejectSubmission(token, sub.id);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Reject failed';
      setErrors((er) => ({ ...er, [sub.id]: msg }));
    } finally {
      setBusy(sub.id, false);
    }
  };

  const counts = SUBMISSION_TABS.reduce((acc, t) => {
    acc[t] = t === 'all' ? submissions.length : submissions.filter((s) => s.status === t).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-slate-500 hover:text-slate-900">
              <ArrowLeft size={16} />
            </Link>
            <Shield size={15} className="text-emerald-600" />
            <span className="font-semibold text-slate-900 text-[15px]">Admin · Submissions</span>
          </div>
          <button
            onClick={onLogout}
            className="text-[12.5px] text-slate-500 hover:text-slate-900"
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-6">
        <AuditPanel token={token} />
        <FeedbackPanel token={token} />

        <div className="flex items-center gap-2 mb-4">
          <Filter size={13} className="text-slate-400" />
          {SUBMISSION_TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-[12.5px] font-medium px-2.5 py-1 rounded-md transition-colors ${
                tab === t ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {t[0].toUpperCase() + t.slice(1)}
              <span className={`ml-1 ${tab === t ? 'text-slate-300' : 'text-slate-400'}`}>
                {counts[t] || 0}
              </span>
            </button>
          ))}
          <button
            onClick={load}
            className="ml-auto text-slate-500 hover:text-slate-900 p-1.5 rounded-md hover:bg-slate-100"
            title="Refresh"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {loading && !submissions.length ? (
          <div className="text-center py-16 text-[13px] text-slate-500">Loading submissions…</div>
        ) : !visible.length ? (
          <div className="text-center py-16 text-[13px] text-slate-500 bg-white rounded-xl border border-slate-200">
            No {tab !== 'all' ? tab : ''} submissions.
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((sub) => (
              <div key={sub.id} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {sub.logo_data_url ? (
                        <img
                          src={sub.logo_data_url}
                          alt={`${sub.name} logo`}
                          data-testid="submission-logo-thumb"
                          className="w-full h-full object-contain p-1"
                        />
                      ) : (
                        <ImageIcon size={18} className="text-slate-300" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900 text-[15px]">{sub.name}</h3>
                      <StatusPill status={sub.status} />
                      {sub.sector && (
                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                          {sub.sector}
                        </span>
                      )}
                    </div>
                    {sub.description && (
                      <p className="mt-1.5 text-[13px] text-slate-600 leading-snug">{sub.description}</p>
                    )}
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1 text-[12px] text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <MapPin size={12} className="text-rose-400" fill="currentColor" />
                        <span className="truncate">{sub.address}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Mail size={12} className="text-slate-400" />
                        <a href={`mailto:${sub.email}`} className="hover:text-slate-900 truncate">{sub.email}</a>
                      </div>
                      {sub.website && (
                        <div className="flex items-center gap-1.5">
                          <Globe size={12} className="text-slate-400" />
                          <a
                            href={`https://${sub.website.replace(/^https?:\/\//, '')}`}
                            target="_blank" rel="noreferrer"
                            className="hover:text-slate-900 truncate inline-flex items-center gap-0.5"
                          >
                            {sub.website}
                            <ExternalLink size={10} />
                          </a>
                        </div>
                      )}
                      <div className="text-slate-400">
                        Submitted {new Date(sub.submittedAt).toLocaleString()}
                      </div>
                    </div>
                    {errors[sub.id] && (
                      <div className="mt-2 text-[12px] text-rose-700">{errors[sub.id]}</div>
                    )}
                    </div>
                  </div>

                  {sub.status === 'pending' && (
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => handleApprove(sub)}
                        disabled={busyIds.has(sub.id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[12px] font-medium bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
                      >
                        {busyIds.has(sub.id) ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(sub)}
                        disabled={busyIds.has(sub.id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[12px] font-medium bg-white border border-slate-200 hover:border-rose-300 hover:text-rose-700 text-slate-600"
                      >
                        <X size={12} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Admin = () => {
  // Login removed per owner request — open the admin dashboard directly.
  return <AdminInner token={'open'} onLogout={() => { window.location.href = '/'; }} />;
};

export default Admin;
